import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Wrench, User, Server, Search, Clock, Hash, Cpu, Thermometer, Zap, MemoryStick, Activity, Gauge } from 'lucide-react';
import { useLLMSessions, useLLMInvocations, useLLMSteps, type LLMInvocation } from '@/hooks/useLLMLogs';
import { useGPUDevices, type GPUDevice } from '@/hooks/useGPUMetrics';
import { Progress } from '@/components/ui/progress';
import { formatDistanceToNow } from 'date-fns';

function StepIcon({ type }: { type: string }) {
  if (type === 'tool_call' || type === 'tool_result') return <Wrench className="h-4 w-4 text-amber-500" />;
  if (type === 'user_message') return <User className="h-4 w-4 text-blue-500" />;
  if (type === 'assistant_message') return <Bot className="h-4 w-4 text-primary" />;
  return <Server className="h-4 w-4 text-muted-foreground" />;
}

function StatBar({ label, value, suffix = '%', max = 100, icon: Icon }: { label: string; value: number | null; suffix?: string; max?: number; icon?: any }) {
  const v = value ?? 0;
  const pct = max > 0 ? Math.min(100, (v / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="flex items-center gap-1.5 text-muted-foreground">
          {Icon && <Icon className="h-3 w-3" />}
          {label}
        </span>
        <span className="font-mono font-medium">
          {value == null ? '—' : `${v.toFixed(suffix === '%' ? 0 : 1)}${suffix}`}
        </span>
      </div>
      <Progress value={pct} className="h-1.5" />
    </div>
  );
}

function GPUCard({ gpu }: { gpu: GPUDevice }) {
  const memTotal = gpu.memory_total_mb ?? 0;
  const memUsed = gpu.memory_used_mb ?? 0;
  const memPct = memTotal > 0 ? (memUsed / memTotal) * 100 : 0;
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4 text-emerald-500" />
              {gpu.name || 'GPU'} <span className="text-muted-foreground text-xs">#{gpu.gpu_index}</span>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {gpu.hostname} · driver {gpu.driver_version || '—'}{gpu.cuda_version ? ` · CUDA ${gpu.cuda_version}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            {gpu.performance_state && <Badge variant="secondary" className="text-xs">{gpu.performance_state}</Badge>}
            {gpu.throttle_reasons && gpu.throttle_reasons !== '0x0000000000000000' && (
              <Badge variant="destructive" className="text-xs">throttle</Badge>
            )}
            <Badge variant="outline" className="text-xs">{gpu.process_count} proc</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <StatBar label="GPU util" value={gpu.utilization_gpu_percent} icon={Activity} />
          <StatBar label="Mem util" value={gpu.utilization_memory_percent} icon={Gauge} />
          <StatBar label="Encoder" value={gpu.utilization_encoder_percent} icon={Activity} />
          <StatBar label="Decoder" value={gpu.utilization_decoder_percent} icon={Activity} />
        </div>

        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <MemoryStick className="h-3 w-3" /> VRAM
            </span>
            <span className="font-mono font-medium">
              {memUsed.toFixed(0)} / {memTotal.toFixed(0)} MiB ({memPct.toFixed(0)}%)
            </span>
          </div>
          <Progress value={memPct} className="h-2" />
          {gpu.memory_reserved_mb != null && gpu.memory_reserved_mb > 0 && (
            <p className="text-[10px] text-muted-foreground mt-1">reserved: {gpu.memory_reserved_mb.toFixed(0)} MiB · free: {(gpu.memory_free_mb ?? 0).toFixed(0)} MiB</p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground flex items-center gap-1"><Thermometer className="h-3 w-3" /> GPU temp</div>
            <div className="font-mono font-medium">{gpu.temperature_gpu_c != null ? `${gpu.temperature_gpu_c.toFixed(0)}°C` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground flex items-center gap-1"><Thermometer className="h-3 w-3" /> Mem temp</div>
            <div className="font-mono font-medium">{gpu.temperature_memory_c != null ? `${gpu.temperature_memory_c.toFixed(0)}°C` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground flex items-center gap-1"><Zap className="h-3 w-3" /> Power</div>
            <div className="font-mono font-medium">
              {gpu.power_draw_w != null ? `${gpu.power_draw_w.toFixed(0)}` : '—'}
              {gpu.power_limit_w != null && <span className="text-muted-foreground"> / {gpu.power_limit_w.toFixed(0)}W</span>}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Fan</div>
            <div className="font-mono font-medium">{gpu.fan_speed_percent != null ? `${gpu.fan_speed_percent.toFixed(0)}%` : '—'}</div>
          </div>

          <div>
            <div className="text-muted-foreground">Graphics clk</div>
            <div className="font-mono font-medium">{gpu.clock_graphics_mhz != null ? `${gpu.clock_graphics_mhz.toFixed(0)} MHz` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">SM clk</div>
            <div className="font-mono font-medium">{gpu.clock_sm_mhz != null ? `${gpu.clock_sm_mhz.toFixed(0)} MHz` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Memory clk</div>
            <div className="font-mono font-medium">{gpu.clock_memory_mhz != null ? `${gpu.clock_memory_mhz.toFixed(0)} MHz` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Video clk</div>
            <div className="font-mono font-medium">{gpu.clock_video_mhz != null ? `${gpu.clock_video_mhz.toFixed(0)} MHz` : '—'}</div>
          </div>

          <div>
            <div className="text-muted-foreground">PCIe</div>
            <div className="font-mono font-medium">
              gen{gpu.pcie_gen_current ?? '—'}/{gpu.pcie_gen_max ?? '—'} x{gpu.pcie_width_current ?? '—'}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">PCIe TX</div>
            <div className="font-mono font-medium">{gpu.pcie_tx_throughput_kbs != null ? `${(gpu.pcie_tx_throughput_kbs / 1024).toFixed(1)} MB/s` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">PCIe RX</div>
            <div className="font-mono font-medium">{gpu.pcie_rx_throughput_kbs != null ? `${(gpu.pcie_rx_throughput_kbs / 1024).toFixed(1)} MB/s` : '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground">ECC err (C/U)</div>
            <div className="font-mono font-medium">
              {(gpu.ecc_errors_corrected_total ?? 0)} / {(gpu.ecc_errors_uncorrected_total ?? 0)}
            </div>
          </div>
        </div>

        {gpu.processes && gpu.processes.length > 0 && (
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1.5">Running processes</div>
            <div className="space-y-1">
              {gpu.processes.slice(0, 6).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs border rounded px-2 py-1 bg-muted/30">
                  <span className="font-mono truncate">{p.process_name} <span className="text-muted-foreground">({p.pid})</span></span>
                  <span className="font-mono">{(p.used_memory_mb ?? 0).toFixed(0)} MiB</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground">
          PCI {gpu.pci_bus_id || '—'} · VBIOS {gpu.vbios_version || '—'} · updated {formatDistanceToNow(new Date(gpu.last_seen), { addSuffix: true })}
        </p>
      </CardContent>
    </Card>
  );
}

function GPUSection({ gpus, loading, onRefresh }: { gpus: GPUDevice[]; loading: boolean; onRefresh: () => void }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="h-4 w-4 text-emerald-500" /> GPU monitoring
            <Badge variant="outline" className="text-xs">{gpus.length} device{gpus.length === 1 ? '' : 's'}</Badge>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={onRefresh}>Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && gpus.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading GPU metrics…</p>
        ) : gpus.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No GPU data yet. Install the Linux agent on a host with NVIDIA GPUs (requires <code>nvidia-smi</code>).
          </p>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {gpus.map(g => <GPUCard key={g.id} gpu={g} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InvocationDetail({ invocation, onClose }: { invocation: LLMInvocation; onClose: () => void }) {
  const { steps, loading } = useLLMSteps(invocation.id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Invocation chain
            <Badge variant="outline">{invocation.model || 'unknown model'}</Badge>
            <Badge variant="secondary">{invocation.steps_count} steps</Badge>
            {invocation.tool_calls_count > 0 && (
              <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30">
                {invocation.tool_calls_count} tool calls
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading steps…</p>
          ) : (
            <div className="space-y-3">
              {steps.map((s) => (
                <div key={s.id} className="border rounded-lg p-3 bg-card">
                  <div className="flex items-center gap-2 mb-2">
                    <StepIcon type={s.step_type} />
                    <span className="text-xs font-mono uppercase text-muted-foreground">{s.step_type}</span>
                    {s.tool_name && <Badge variant="outline" className="text-xs">{s.tool_name}</Badge>}
                    {s.tokens != null && <Badge variant="secondary" className="text-xs ml-auto">{s.tokens} tok</Badge>}
                  </div>
                  {s.tool_arguments && (
                    <pre className="text-xs bg-muted/50 p-2 rounded mb-2 overflow-auto max-h-40">
                      {JSON.stringify(s.tool_arguments, null, 2)}
                    </pre>
                  )}
                  {s.content && (
                    <pre className="text-sm whitespace-pre-wrap font-sans">{s.content}</pre>
                  )}
                  {s.tool_output && !s.content && (
                    <pre className="text-xs bg-muted/30 p-2 rounded overflow-auto max-h-40">{s.tool_output}</pre>
                  )}
                </div>
              ))}
              {steps.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">No step details captured.</p>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function LLMMonitoringPage() {
  const [search, setSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedInvocation, setSelectedInvocation] = useState<LLMInvocation | null>(null);
  const { sessions, loading: sLoading } = useLLMSessions();
  const { invocations, loading: iLoading, refetch } = useLLMInvocations({
    sessionId: selectedSession || undefined,
    search: search || undefined,
  });
  const { devices: gpus, loading: gLoading, refetch: refetchGpus } = useGPUDevices(10000);

  const stats = useMemo(() => ({
    totalSessions: sessions.length,
    totalInvocations: invocations.length,
    totalToolCalls: invocations.reduce((a, i) => a + i.tool_calls_count, 0),
    totalTokens: invocations.reduce((a, i) => a + i.total_tokens, 0),
  }), [sessions, invocations]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Bot className="h-7 w-7" /> LLM Monitoring
        </h1>
        <p className="text-muted-foreground">Per-invocation logs from OpenWebUI including tool call chains.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalSessions}</div><p className="text-xs text-muted-foreground">Chat sessions</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalInvocations}</div><p className="text-xs text-muted-foreground">Invocations</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalToolCalls}</div><p className="text-xs text-muted-foreground">Tool calls</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalTokens.toLocaleString()}</div><p className="text-xs text-muted-foreground">Tokens used</p></CardContent></Card>
      </div>

      <GPUSection gpus={gpus} loading={gLoading} onRefresh={refetchGpus} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Chat sessions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1 max-h-[600px] overflow-auto">
              <button
                onClick={() => setSelectedSession(null)}
                className={`w-full text-left p-2 rounded text-sm hover:bg-muted ${!selectedSession ? 'bg-muted font-medium' : ''}`}
              >
                All sessions
              </button>
              {sLoading ? <p className="text-xs text-muted-foreground p-2">Loading…</p> : sessions.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSession(s.id)}
                  className={`w-full text-left p-2 rounded hover:bg-muted ${selectedSession === s.id ? 'bg-muted' : ''}`}
                >
                  <div className="text-sm font-medium truncate">{s.user_name || 'anon'} · {s.model || '—'}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Hash className="h-3 w-3" />{s.message_count} msgs
                    <Clock className="h-3 w-3" />{formatDistanceToNow(new Date(s.last_activity_at), { addSuffix: true })}
                  </div>
                </button>
              ))}
              {!sLoading && sessions.length === 0 && (
                <p className="text-xs text-muted-foreground p-4 text-center">No sessions yet. Install the OpenWebUI filter to start.</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">Invocations</CardTitle>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Search prompts/responses…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-8 w-64"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={refetch}>Refresh</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[600px] overflow-auto">
              {iLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : invocations.map(inv => (
                <button
                  key={inv.id}
                  onClick={() => setSelectedInvocation(inv)}
                  className="w-full text-left border rounded-lg p-3 hover:bg-muted transition"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">{inv.model || '—'}</Badge>
                      {inv.tool_calls_count > 0 && (
                        <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-xs">
                          <Wrench className="h-3 w-3 mr-1" />{inv.tool_calls_count} tools
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">{inv.steps_count} steps</Badge>
                      {inv.status !== 'completed' && <Badge variant="destructive" className="text-xs">{inv.status}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(inv.started_at), { addSuffix: true })}
                    </span>
                  </div>
                  <div className="text-sm line-clamp-2"><span className="font-medium">Q: </span>{inv.user_prompt || '—'}</div>
                  {inv.final_response && (
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-1"><span className="font-medium">A: </span>{inv.final_response}</div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    <span>{inv.total_tokens.toLocaleString()} tokens</span>
                    <span>{(inv.duration_ms / 1000).toFixed(2)}s</span>
                    <span className="truncate">{inv.user_name || 'anon'}</span>
                  </div>
                </button>
              ))}
              {!iLoading && invocations.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-12">No invocations match your filter.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {selectedInvocation && (
        <InvocationDetail invocation={selectedInvocation} onClose={() => setSelectedInvocation(null)} />
      )}
    </div>
  );
}